import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OpenAndClosedRevenuesService {
  private apiUrl = `${environment.main_url}/agilelegal/reports/tasks/by-revenue`;
  private practiceAreasUrl = `${environment.main_url}/agilelegal/practiceArea`;
  private organizationsUrl = `${environment.main_url}/agilelegal/organizations/names`;
  private mattersUrl = `${environment.main_url}/agilelegal/matters/names`;

  constructor(private http: HttpClient) { }

  getRevenueData(filters: any): Observable<any> {
    let params = new HttpParams();
    
    // Add all filter parameters to the query, only if they have actual values
    if (filters.startDate && filters.startDate !== '') {
      params = params.set('startDate', filters.startDate);
    }
    if (filters.endDate && filters.endDate !== '') {
      params = params.set('endDate', filters.endDate);
    }
    if (filters.organization && filters.organization !== '') {
      params = params.set('organization', filters.organization);
    }
    if (filters.matter && filters.matter !== '') {
      params = params.set('matter', filters.matter);
    }
    if (filters.practiceArea && filters.practiceArea !== '') {
      params = params.set('practiceArea', filters.practiceArea);
    }
    if (filters.createdBy && filters.createdBy !== '') {
      params = params.set('createdBy', filters.createdBy);
    }
    if (filters.projectName && filters.projectName !== '') {
      params = params.set('projectName', filters.projectName);
    }
    if (filters.projectTypeCode && filters.projectTypeCode !== '') {
      params = params.set('projectTypeCode', filters.projectTypeCode);
    }
    
    return this.http.get<any>(this.apiUrl, { params })
      .pipe(
        catchError(error => {
          console.error('Error fetching revenue data:', error);
          return of({ 
            data: [], 
            success: false, 
            message: 'Failed to fetch revenue data' 
          });
        })
      );
  }
  
  getPracticeAreas(): Observable<any> {
    return this.http.get<any>(this.practiceAreasUrl)
      .pipe(
        catchError(error => {
          console.error('Error fetching practice areas:', error);
          return of({ 
            data: [], 
            success: false, 
            message: 'Failed to fetch practice areas' 
          });
        })
      );
  }

  getOrganizationNames(): Observable<any> {
    return this.http.get<any>(this.organizationsUrl)
      .pipe(
        map(response => {
          // Transform data for ng-select format (id/name pairs)
          // Since we only have organization names, we'll use the name as both id and name
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((name: string, index: number) => ({
                id: name,
                name: name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching organization names:', error);
          return of({ 
            data: [], 
            success: false, 
            message: 'Failed to fetch organization names' 
          });
        })
      );
  }

  getMatterNames(organization?: string): Observable<any> {
    let url = this.mattersUrl;
    
    // If organization is provided, add it as a query parameter
    if (organization) {
      url = `${this.mattersUrl}?organization=${encodeURIComponent(organization)}`;
    }
    
    return this.http.get<any>(url)
      .pipe(
        map(response => {
          // Transform data for ng-select format (id/name pairs)
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((name: string) => ({
                id: name,
                name: name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching matter names:', error);
          return of({ 
            data: [], 
            success: false, 
            message: 'Failed to fetch matter names' 
          });
        })
      );
  }
}
